import React, { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://rakeen-whatsapp-poc.thamer-21d.workers.dev';

function changeClass(current, previous) {
  if (previous === 0 && current > 0) return 'rp-change-up';
  const diff = current - previous;
  if (diff > 0) return 'rp-change-up';
  if (diff < 0) return 'rp-change-down';
  return 'rp-change-same';
}

function changeText(current, previous) {
  if (previous === 0 && current === 0) return '';
  if (previous === 0 && current > 0) return '⬆ جديد';
  const diff = current - previous;
  if (diff === 0) return '← ثابت';
  const pct = Math.round((diff / previous) * 100);
  return `${diff > 0 ? '⬆' : '⬇'} ${Math.abs(pct)}%`;
}

function ReportsPage({ idToken, role }) {
  const [period, setPeriod] = useState('daily');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${WORKER_URL}/api/reports/${period}`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (res.ok) setReport(await res.json());
    } catch (err) {
      console.error('Failed to fetch report:', err);
    }
    setLoading(false);
  }, [idToken, period]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const sendEmail = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${WORKER_URL}/api/reports/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ period }),
      });
      const data = await res.json();
      setSendResult(data);
    } catch (err) {
      setSendResult({ success: false, message: 'فشل الإرسال' });
    }
    setSending(false);
  };

  if (loading) {
    return <div className="sd-stats-loading">جاري تحميل التقرير...</div>;
  }

  if (!report) {
    return <div className="sd-stats-loading">لا توجد بيانات</div>;
  }

  const c = report.current;
  const p = report.previous;
  const pr = report.products;
  const periodLabel = period === 'daily' ? 'اليومي' : 'الأسبوعي';

  return (
    <div className="rp-container">
      {/* Header */}
      <div className="rp-header">
        <div>
          <h2 className="rp-title">📊 التقرير {periodLabel} — {report.dayName}</h2>
          <div className="rp-dates">
            <span className="rp-hijri">{report.hijriLabel}</span>
            <span className="rp-gregorian">{report.dateLabel}</span>
          </div>
        </div>
        <div className="rp-actions">
          <div className="rp-period-toggle">
            <button className={`rp-period-btn ${period === 'daily' ? 'active' : ''}`} onClick={() => setPeriod('daily')}>يومي</button>
            <button className={`rp-period-btn ${period === 'weekly' ? 'active' : ''}`} onClick={() => setPeriod('weekly')}>أسبوعي</button>
          </div>
          {role === 'admin' && (
            <button className="rp-send-btn" onClick={sendEmail} disabled={sending}>
              {sending ? '...جاري الإرسال' : '📧 أرسل بالإيميل'}
            </button>
          )}
        </div>
      </div>

      {sendResult && (
        <div className={`rp-send-result ${sendResult.success ? 'rp-send-ok' : 'rp-send-fail'}`}>
          {sendResult.message}
        </div>
      )}

      {/* KPI Cards */}
      <div className="rp-kpi-grid">
        <div className="rp-kpi rp-kpi-blue">
          <div className="rp-kpi-value">{c.conversations}</div>
          <div className={`rp-kpi-change ${changeClass(c.conversations, p.conversations)}`}>{changeText(c.conversations, p.conversations)}</div>
          <div className="rp-kpi-label">محادثات</div>
        </div>
        <div className="rp-kpi rp-kpi-green">
          <div className="rp-kpi-value">{c.ordersConfirmed}</div>
          <div className={`rp-kpi-change ${changeClass(c.ordersConfirmed, p.ordersConfirmed)}`}>{changeText(c.ordersConfirmed, p.ordersConfirmed)}</div>
          <div className="rp-kpi-label">طلبات مؤكدة</div>
        </div>
        <div className="rp-kpi rp-kpi-yellow">
          <div className="rp-kpi-value">{c.quotesSent}</div>
          <div className={`rp-kpi-change ${changeClass(c.quotesSent, p.quotesSent)}`}>{changeText(c.quotesSent, p.quotesSent)}</div>
          <div className="rp-kpi-label">عروض سعر</div>
        </div>
        <div className="rp-kpi rp-kpi-red">
          <div className="rp-kpi-value">{c.abandoned}</div>
          <div className={`rp-kpi-change ${changeClass(c.abandoned, p.abandoned)}`}>{changeText(c.abandoned, p.abandoned)}</div>
          <div className="rp-kpi-label">محادثات متوقفة</div>
        </div>
      </div>

      {/* Products Summary */}
      <div className="rp-section">
        <h3 className="rp-section-title">المنتجات ({pr.total})</h3>
        <div className="rp-products-bar">
          <span className="rp-prod-stat rp-prod-in">✅ متوفر: {pr.inStock}</span>
          <span className="rp-prod-stat rp-prod-low">⚠️ قليل: {pr.lowStock}</span>
          <span className="rp-prod-stat rp-prod-out">❌ نفذ: {pr.outOfStock}</span>
          <span className="rp-prod-stat rp-prod-untracked">➖ غير محدد: {pr.untracked}</span>
        </div>
      </div>

      {/* Status Breakdown */}
      {c.statusBreakdown.length > 0 && (
        <div className="rp-section">
          <h3 className="rp-section-title">حالة الطلبات</h3>
          <div className="rp-table">
            {c.statusBreakdown.map((s) => (
              <div key={s.status} className="rp-table-row">
                <span className="rp-table-label">{s.label}</span>
                <span className="rp-table-value">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Categories */}
      {c.topCategories.length > 0 && (
        <div className="rp-section">
          <h3 className="rp-section-title">أكثر الأقسام طلباً</h3>
          <div className="rp-table">
            {c.topCategories.map((cat, i) => (
              <div key={cat.name} className="rp-table-row">
                <span className="rp-table-label">{i + 1}. {cat.name}</span>
                <span className="rp-table-value">{cat.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Abandoned Details */}
      {c.abandonedSteps.length > 0 && (
        <div className="rp-section">
          <h3 className="rp-section-title">المحادثات المتوقفة ({c.abandoned})</h3>
          <div className="rp-table">
            {c.abandonedSteps.map((a) => (
              <div key={a.step} className="rp-table-row">
                <span className="rp-table-label">{a.step}</span>
                <span className="rp-table-value">{a.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <div className="rp-section">
        <h3 className="rp-section-title">مقارنة مع {period === 'daily' ? 'أول أمس' : 'الأسبوع الماضي'}</h3>
        <div className="rp-comparison">
          <div className="rp-comp-header">
            <span></span>
            <span>{period === 'daily' ? 'بالأمس' : 'هذا الأسبوع'}</span>
            <span>{period === 'daily' ? 'أول أمس' : 'الأسبوع الماضي'}</span>
            <span>التغيير</span>
          </div>
          {[
            { label: 'محادثات', cur: c.conversations, prev: p.conversations },
            { label: 'عروض سعر', cur: c.quotesSent, prev: p.quotesSent },
            { label: 'طلبات مؤكدة', cur: c.ordersConfirmed, prev: p.ordersConfirmed },
            { label: 'دخول لوحة التحكم', cur: c.dashboardLogins, prev: p.dashboardLogins },
            { label: 'ردود مرسلة', cur: c.repliesSent, prev: p.repliesSent },
          ].map((row) => (
            <div key={row.label} className="rp-comp-row">
              <span className="rp-comp-label">{row.label}</span>
              <span className="rp-comp-value">{row.cur}</span>
              <span className="rp-comp-value rp-comp-prev">{row.prev}</span>
              <span className={`rp-comp-change ${changeClass(row.cur, row.prev)}`}>{changeText(row.cur, row.prev)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ReportsPage;
